// @flow

/**
 * Module dependencies.
 */

import { Col, Row } from 'client/components/core/grid';
import { LinkIconSecondary } from 'client/components/core/buttons/links';
import { branch } from 'core/utils';
import { colors, media, units } from 'client/styles';
import { isEmpty } from 'lodash';
import Icon from 'client/components/core/icon';
import Layout from 'client/components/core/layout';
import RawHtml from 'react-raw-html';
import React from 'react';
import Text from 'client/components/core/type';
import styled from 'styled-components';

/**
 * `Labels` type.
 */

type Labels = {
  address: string,
  europePhone: string,
  link: string,
  phone: string
};

/**
 * `Contact` type.
 */

type Contact = {
  address: string,
  key: string,
  link: string,
  phone: string,
  title: string
};

/**
 * `Props` type.
 */

type Props = {
  contacts: Array<Contact>,
  labels: Labels
};

/**
 * `ContentAlign` component.
 */

const ContentAlign = styled(Layout)`
  padding-right: ${units(6)};
  padding-top: ${units(1)};
`;

/**
 * `TextSmall` component.
 */

// $FlowFixMe
const TextSmall = styled(Text.Small)`
  ${media.min.md`display: block;`}
`;

/**
 * Render contacts.
 */

const renderContacts = (items: Array<Contact>, labels: Labels) => items
  .map((contact: Contact, index: number): React$Element<*> => (
    <Layout
      key={index}
      paddingVertical={units(6)}
      paddingVerticalSm={units(4)}
    >
      <Row>
        <Col
          lg={9}
          xs={10}
          xsOffset={1}
        >
          <Row>
            <Col
              lg={4}
              xs={12}
            >
              <Text.H3 color={colors.brandPrimary}>
                <RawHtml.span>
                  {contact.title}
                </RawHtml.span>
              </Text.H3>
            </Col>

            <Col
              lg={8}
              sm={11}
              xs={12}
            >
              <Row>
                <Col
                  sm={6}
                  xs={12}
                >
                  <ContentAlign>
                    <TextSmall
                      color={colors.brandPrimary}
                      weight={700}
                    >
                      {labels.address}
                    </TextSmall>

                    <RawHtml.div>
                      {contact.address}
                    </RawHtml.div>
                  </ContentAlign>
                </Col>

                <Col
                  sm={3}
                  xs={12}
                >
                  <ContentAlign
                    paddingBottom={units(3)}
                    paddingBottomSm={0}
                  >
                    <TextSmall
                      color={colors.brandPrimary}
                      weight={700}
                    >
                      {contact.key === 'eur' ? labels.europePhone : labels.phone}
                    </TextSmall>

                    <Layout whiteSpace={'nowrap'}>
                      <RawHtml.div>
                        {contact.phone}
                      </RawHtml.div>
                    </Layout>
                  </ContentAlign>
                </Col>

                <Col
                  sm={3}
                  xs={12}
                >
                  {branch(!isEmpty(contact.link),
                    <Layout
                      paddingTopSm={units(5)}
                      textAlignSm={'right'}
                    >
                      <LinkIconSecondary
                        target={'_blank'}
                        to={contact.link}
                      >
                        {labels.link}

                        <Icon
                          icon={'arrow-circle'}
                          size={'28px'}
                        />
                      </LinkIconSecondary>
                    </Layout>
                  )}
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Layout>
  ));

/**
 * `GeneralContacts` component.
 */

function GeneralContacts(props: Props): React$Element<*> {
  const { contacts, labels } = props;

  return branch(contacts.length,
    <Layout
      paddingBottom={units(2)}
      paddingTop={units(10)}
    >
      {renderContacts(contacts, labels)}
    </Layout>
  );
}

/**
 * Export `GeneralContacts`.
 */

export default GeneralContacts;



// WEBPACK FOOTER //
// ../src/client/components/contacts/general-contacts.js